import React, { createContext, useContext, useState, useCallback, useRef } from "react";
import type { Toast } from "primereact/toast";

export type ToastSeverity = "success" | "error" | "warn" | "info" | "secondary" | "contrast";

export interface ToastOptions {
  severity: ToastSeverity;
  summary: string;
  detail?: string;
  life?: number;
}

export interface AppLayoutContextValue {
  /** Show or hide the global loader overlay */
  setLoader: (active: boolean) => void;
  /** Whether the loader is currently active */
  isLoading: boolean;
  /** Show a toast message */
  showToast: (options: ToastOptions | string, severity?: ToastSeverity) => void;
  /** Ref to the Toast component - used internally by AppLayout */
  toastRef: React.RefObject<Toast | null>;
}

const AppLayoutContext = createContext<AppLayoutContextValue | null>(null);

export function useAppLayout(): AppLayoutContextValue {
  const context = useContext(AppLayoutContext);
  if (!context) {
    throw new Error("useAppLayout must be used within AppLayoutProvider");
  }
  return context;
}

interface AppLayoutProviderProps {
  children: React.ReactNode;
}

export function AppLayoutProvider({ children }: AppLayoutProviderProps) {
  const [isLoading, setLoading] = useState(false);
  const toastRef = useRef<Toast | null>(null);

  const setLoader = useCallback((active: boolean) => {
    setLoading(active);
  }, []);

  const showToast = useCallback(
    (options: ToastOptions | string, severity?: ToastSeverity) => {
      const message =
        typeof options === "string"
          ? { severity: (severity ?? "info") as ToastSeverity, summary: options }
          : {
              severity: options.severity,
              summary: options.summary,
              detail: options.detail,
              life: options.life ?? 3000,
            };
      toastRef.current?.show(message);
    },
    []
  );

  const value: AppLayoutContextValue = {
    setLoader,
    isLoading,
    showToast,
    toastRef,
  };

  return (
    <AppLayoutContext.Provider value={value}>{children}</AppLayoutContext.Provider>
  );
}
